import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { TrashIcon, EyeIcon, EyeSlashIcon, PlusIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import getRealtimeDeploymentController from "./realtime-deployment-controller"

const PATH_REGEX = /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*$/
const MAX_DEPTH = 6
const SECRET_KEY_REGEX = /(api[_-]?key|secret|token|password|passphrase|private[_-]?key)$/i
const DEPLOYMENT_PREFIX = "deployment."

const flatten = (obj, prefix = "", out = []) => {
  if (!obj || typeof obj !== "object") return out
  for (const [k, v] of Object.entries(obj)) {
    const next = prefix ? `${prefix}.${k}` : k
    if (v && typeof v === "object" && !Array.isArray(v)) {
      flatten(v, next, out)
    } else {
      out.push({ path: next, value: v == null ? "" : String(v) })
    }
  }
  return out
}

const isValidPath = (p) =>
  typeof p === "string" &&
  PATH_REGEX.test(p) &&
  p.split(".").length <= MAX_DEPTH

const isSensitivePath = (p) =>
  typeof p === "string" && p.startsWith(DEPLOYMENT_PREFIX)

const SettingRow = ({ row, draftValue, onChange, onDelete, onUndo, markedForDelete, busy }) => {
  const [revealed, setRevealed] = useState(false)
  const isSecret = SECRET_KEY_REGEX.test(row.path.split(".").pop() || "")
  const dirty = draftValue !== undefined && draftValue !== row.value

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 border-b border-admin_bordered last:border-b-0 ${
        markedForDelete ? "opacity-40 line-through" : ""
      }`}
    >
      <span className="text-xs font-mono text-admin_text_secondary truncate basis-1/3 min-w-0">
        {row.path}
      </span>
      <input
        type={isSecret && !revealed ? "password" : "text"}
        value={draftValue !== undefined ? draftValue : row.value}
        disabled={busy || markedForDelete}
        onChange={(e) => onChange(row.path, e.target.value)}
        className={`flex-1 min-w-0 text-xs font-mono bg-admin_canvas border rounded px-2 py-1 text-admin_text focus:outline-none focus:border-admin_primary ${
          dirty ? "border-admin_warning" : "border-admin_bordered"
        }`}
      />
      {isSecret && (
        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          className="text-admin_text_secondary hover:text-admin_text shrink-0"
          aria-label={revealed ? "Hide value" : "Reveal value"}
        >
          {revealed ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
        </button>
      )}
      {markedForDelete ? (
        <button
          type="button"
          onClick={() => onUndo(row.path)}
          disabled={busy}
          className="text-admin_text_secondary hover:text-admin_text shrink-0 disabled:opacity-40"
          aria-label="Undo delete"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onDelete(row.path)}
          disabled={busy}
          className="text-admin_text_secondary hover:text-admin_error shrink-0 disabled:opacity-40"
          aria-label="Delete setting"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

const AddRow = ({ existingPaths, onAdd, busy }) => {
  const [path, setPath] = useState("")
  const [value, setValue] = useState("")
  const [error, setError] = useState(null)

  const submit = () => {
    if (!path) {
      setError("path required")
      return
    }
    if (!isValidPath(path)) {
      setError("invalid path — use a.b.c with [a-zA-Z0-9_] segments, max depth 6")
      return
    }
    if (existingPaths.includes(path)) {
      setError("path already exists — edit the existing row")
      return
    }
    setError(null)
    onAdd(path, value)
    setPath("")
    setValue("")
  }

  return (
    <div className="px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="path (e.g. status.api_key)"
          value={path}
          disabled={busy}
          onChange={(e) => {
            setPath(e.target.value)
            if (error) setError(null)
          }}
          className="basis-1/3 min-w-0 text-xs font-mono bg-admin_canvas border border-admin_bordered rounded px-2 py-1 text-admin_text focus:outline-none focus:border-admin_primary"
        />
        <input
          type="text"
          placeholder="value"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="flex-1 min-w-0 text-xs font-mono bg-admin_canvas border border-admin_bordered rounded px-2 py-1 text-admin_text focus:outline-none focus:border-admin_primary"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !path}
          className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border border-admin_primary/40 text-admin_primary hover:bg-admin_primary/10 disabled:opacity-40 shrink-0"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
      {error && (
        <p className="text-[11px] text-admin_error">{error}</p>
      )}
    </div>
  )
}

const AppSettingsTab = ({ item }) => {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // pending edits keyed by path: string -> string (new or modified value)
  const [drafts, setDrafts] = useState({})
  // paths queued for deletion
  const [deletes, setDeletes] = useState(() => new Set())
  // paths added via the Add row (subset of drafts that don't exist in settings yet)
  const [additions, setAdditions] = useState(() => new Set())
  const controllerRef = useRef(null)

  const getSocket = () => {
    if (!controllerRef.current) controllerRef.current = getRealtimeDeploymentController()
    return controllerRef.current?.socket
  }

  const resetPending = () => {
    setDrafts({})
    setDeletes(new Set())
    setAdditions(new Set())
  }

  const load = useCallback(() => {
    const socket = getSocket()
    if (!socket) {
      setError("Realtime not connected")
      setLoading(false)
      return
    }
    setLoading(true)
    socket.emit("deployment:get_app_settings", { app_id: item.id }, (response) => {
      if (response?.success) {
        const payload = response.data || {}
        if (payload.result === false) {
          setError(payload.error || "Failed to load settings")
          setSettings({})
        } else {
          setError(null)
          setSettings(payload.settings || {})
        }
      } else {
        setError(response?.error || "Failed to load settings")
        setSettings({})
      }
      setLoading(false)
      resetPending()
    })
  }, [item.id])

  useEffect(() => {
    load()
  }, [load])

  // live updates from server broadcasts — refresh base settings, keep drafts as-is
  useEffect(() => {
    const socket = getSocket()
    if (!socket) return
    const handler = (data) => {
      if (data?.app_id === item.id) {
        setSettings(data.settings || {})
      }
    }
    socket.on("deployment:app_settings_updated", handler)
    return () => {
      socket.off("deployment:app_settings_updated", handler)
    }
  }, [item.id])

  const baseRows = useMemo(() => (settings ? flatten(settings) : []), [settings])
  const baseRowMap = useMemo(() => {
    const m = new Map()
    baseRows.forEach((r) => m.set(r.path, r))
    return m
  }, [baseRows])

  // rows to render: base rows + addition rows (in drafts but not in base)
  const additionRows = useMemo(
    () =>
      Array.from(additions).map((p) => ({
        path: p,
        value: "",
      })),
    [additions]
  )
  const rows = [...baseRows, ...additionRows]
  const existingPaths = rows.map((r) => r.path)

  const handleDraftChange = (path, value) => {
    setDrafts((d) => ({ ...d, [path]: value }))
  }

  const handleDelete = (path) => {
    setDeletes((s) => {
      const n = new Set(s)
      n.add(path)
      return n
    })
    // if it's a pending addition, just drop it
    if (additions.has(path)) {
      setAdditions((s) => {
        const n = new Set(s)
        n.delete(path)
        return n
      })
      setDrafts((d) => {
        const n = { ...d }
        delete n[path]
        return n
      })
      setDeletes((s) => {
        const n = new Set(s)
        n.delete(path)
        return n
      })
    }
  }

  const handleUndoDelete = (path) => {
    setDeletes((s) => {
      const n = new Set(s)
      n.delete(path)
      return n
    })
  }

  const handleAdd = (path, value) => {
    setAdditions((s) => {
      const n = new Set(s)
      n.add(path)
      return n
    })
    setDrafts((d) => ({ ...d, [path]: value }))
  }

  // compute what would actually change on save
  const pendingChanges = useMemo(() => {
    const sets = []
    const dels = []
    for (const [path, value] of Object.entries(drafts)) {
      const base = baseRowMap.get(path)
      const isAddition = additions.has(path)
      if (isAddition || !base || base.value !== value) {
        sets.push({ path, value })
      }
    }
    for (const path of deletes) {
      if (additions.has(path)) continue
      dels.push(path)
    }
    return { sets, dels }
  }, [drafts, baseRowMap, additions, deletes])

  const hasPending = pendingChanges.sets.length > 0 || pendingChanges.dels.length > 0
  const touchesDeployment = useMemo(
    () =>
      pendingChanges.sets.some((c) => isSensitivePath(c.path)) ||
      pendingChanges.dels.some((p) => isSensitivePath(p)),
    [pendingChanges]
  )

  const emit = (event, payload) =>
    new Promise((resolve) => {
      const socket = getSocket()
      if (!socket) return resolve({ success: false, error: "Realtime not connected" })
      socket.emit(event, payload, (response) => resolve(response || {}))
    })

  const performSave = async () => {
    setBusy(true)
    setError(null)
    try {
      let latest = null
      for (const { path, value } of pendingChanges.sets) {
        const resp = await emit("deployment:set_app_setting", {
          app_id: item.id,
          path,
          value,
        })
        if (!resp.success || resp.data?.result === false) {
          const msg = resp.data?.error || resp.error || `Failed to save ${path}`
          setError(msg)
          return
        }
        latest = resp.data?.settings ?? latest
      }
      for (const path of pendingChanges.dels) {
        const resp = await emit("deployment:delete_app_setting", {
          app_id: item.id,
          path,
        })
        if (!resp.success || resp.data?.result === false) {
          const msg = resp.data?.error || resp.error || `Failed to delete ${path}`
          setError(msg)
          return
        }
        latest = resp.data?.settings ?? latest
      }
      if (latest) setSettings(latest)
      resetPending()
      const total = pendingChanges.sets.length + pendingChanges.dels.length
      showNotification({
        message: `Saved ${total} change${total !== 1 ? "s" : ""}`,
        className: "bg-admin_success text-white",
      })
    } finally {
      setBusy(false)
    }
  }

  const handleSaveClick = () => {
    if (!hasPending || busy) return
    if (touchesDeployment) {
      showPromptDialog({
        title: "Change deployment config",
        message: "You're changing deployment config. Please verify your intent.",
        confirmText: "Proceed",
        cancelText: "Go Back",
        confirmVariant: "warning_m",
        onConfirm: performSave,
      })
      return
    }
    performSave()
  }

  const handleDiscard = () => {
    if (busy) return
    resetPending()
    setError(null)
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="px-3 py-2 text-xs text-admin_error bg-admin_error/10 border border-admin_error/30 rounded">
          {error}
        </div>
      )}

      <div className="bg-admin_canvas border border-admin_bordered rounded">
        {loading ? (
          <div className="px-3 py-4 text-xs text-admin_text_secondary">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-4 text-xs text-admin_text_secondary italic">
            No settings yet. Add one below.
          </div>
        ) : (
          <div>
            {rows.map((row) => (
              <SettingRow
                key={row.path}
                row={row}
                draftValue={drafts[row.path]}
                markedForDelete={deletes.has(row.path)}
                onChange={handleDraftChange}
                onDelete={handleDelete}
                onUndo={handleUndoDelete}
                busy={busy}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bg-admin_canvas border border-admin_bordered rounded">
        <AddRow existingPaths={existingPaths} onAdd={handleAdd} busy={busy} />
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-admin_text_secondary">
          {hasPending
            ? `${pendingChanges.sets.length} to set, ${pendingChanges.dels.length} to delete`
            : "No pending changes"}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDiscard}
            disabled={!hasPending || busy}
            className="text-xs font-medium px-3 py-1.5 rounded border border-admin_bordered text-admin_text_secondary hover:bg-admin_canvas disabled:opacity-40"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={!hasPending || busy}
            className="text-xs font-medium px-3 py-1.5 rounded bg-admin_primary text-white hover:bg-admin_primary/90 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AppSettingsTab
